import { useCallback, useEffect, useMemo, useRef } from "react";
import JoditEditor, { Jodit } from "jodit-react";
import { Box } from "@mui/material";

// Same editor options as the previous CKEditor setup (toolbar / heading use the CKEditor config format)
export const DEFAULT_EDITOR_CONFIG = {
    toolbar: ["undo", "redo", "|", "heading", "|", "bold", "italic", "|", "link", "imageUpload", "insertTable", "blockQuote", "mediaEmbed", "|", "bulletedList", "numberedList", "outdent", "indent"],
    heading: {
        options: [
            { model: "paragraph", title: "Paragraph" },
            { model: "heading1", view: "h2", title: "Heading 1" },
            { model: "heading2", view: "h3", title: "Heading 2" },
            { model: "heading3", view: "h4", title: "Heading 3" },
        ]
    },
    placeholder: "",
};

// CKEditor toolbar item -> Jodit button
const TOOLBAR_MAP = {
    "|": "|", "-": "\n", undo: "undo", redo: "redo", heading: "paragraph", bold: "bold", italic: "italic", underline: "underline",
    strikethrough: "strikethrough", link: "link", insertTable: "table", blockQuote: "blockquote", mediaEmbed: "video", imageUpload: "imageUrl",
    bulletedList: "ul", numberedList: "ol", outdent: "outdent", indent: "indent", alignment: "align", horizontalLine: "hr", sourceEditing: "source",
};

Jodit.modules.Icon.set("blockquote", '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M3 10.423a6.5 6.5 0 0 1 6.056-6.408l.038.67C6.448 5.423 5.354 7.663 5.22 10H9c.552 0 .5.432.5.986v4.511c0 .554-.448.503-1 .503h-5c-.552 0-.5-.449-.5-1.003v-4.574zm8 0a6.5 6.5 0 0 1 6.056-6.408l.038.67c-2.646.739-3.74 2.979-3.873 5.315H17c.552 0 .5.432.5.986v4.511c0 .554-.448.503-1 .503h-5c-.552 0-.5-.449-.5-1.003v-4.574z"/></svg>');

const getHeadingList = (headingConfig) => (headingConfig?.options || DEFAULT_EDITOR_CONFIG.heading.options).reduce((list, option) => {
    const tag = option.model === "paragraph" ? "p" : (option.view?.name || option.view || `h${Number(option.model.replace("heading", "")) + 1}`);
    return { ...list, [tag]: option.title };
}, {});

// Image from a web URL with alt text and caption, saved like CKEditor: <figure class="image"><img><figcaption></figcaption></figure>
const imageUrlControl = {
    icon: "image",
    tooltip: "Insert image",
    tags: ["img"],
    popup: (editor, current, close) => {
        const doc = editor.od;
        const currentImage = current && current.nodeType === 1 ? (current.tagName === "IMG" ? current : current.querySelector?.("img")) : null;
        const currentFigure = currentImage?.closest("figure");
        const currentCaption = currentFigure?.querySelector("figcaption");
        editor.s.save();

        const form = doc.createElement("form");
        form.style.cssText = `display:flex;flex-direction:column;gap:8px;padding:10px;width:300px;font:13px ${CK.font};color:${CK.text}`;
        const field = (label, value, placeholder) => {
            const wrapper = doc.createElement("label");
            wrapper.style.cssText = "display:flex;flex-direction:column;gap:4px;color:hsl(0, 0%, 44%);font-size:12px";
            wrapper.textContent = label;
            const input = doc.createElement("input");
            input.type = "text";
            input.value = value || "";
            input.placeholder = placeholder;
            input.style.cssText = `height:30px;padding:0 8px;border:1px solid ${CK.border};border-radius:2px;font:13px ${CK.font};color:${CK.text};outline:none`;
            input.addEventListener("focus", () => { input.style.borderColor = CK.focus; });
            input.addEventListener("blur", () => { input.style.borderColor = CK.border; });
            wrapper.appendChild(input);
            form.appendChild(wrapper);
            return input;
        };
        const urlInput = field("Image URL", currentImage?.getAttribute("src"), "https://");
        const altInput = field("Alternative text", currentImage?.getAttribute("alt"), "Describe the image");
        const captionInput = field("Caption", currentCaption?.textContent, "Optional");
        const error = doc.createElement("div");
        error.style.cssText = "color:hsl(15, 100%, 43%);font-size:12px;display:none";
        form.appendChild(error);
        const submit = doc.createElement("button");
        submit.type = "submit";
        submit.textContent = currentImage ? "Update" : "Insert";
        submit.style.cssText = `align-self:flex-end;height:30px;padding:0 14px;border:none;border-radius:2px;background:${CK.focus};color:#fff;font:13px ${CK.font};cursor:pointer`;
        form.appendChild(submit);

        form.addEventListener("submit", async event => {
            event.preventDefault();
            let url = urlInput.value.trim();
            if (/^[a-z\d_-]+(\.[a-z\d_-]+)+/i.test(url)) url = `https://${url}`;
            if (!/^(https?:)?\/\//i.test(url)) {
                error.textContent = "Please enter a valid image URL (http:// or https://).";
                error.style.display = "block";
                return;
            }
            // Make sure the URL really is an image (not e.g. the web page the image is shown on)
            error.style.display = "none";
            submit.disabled = true;
            submit.textContent = "Checking...";
            const isImage = await new Promise(resolve => {
                const probe = new window.Image();
                const timer = setTimeout(() => resolve(false), 10000);
                probe.onload = () => { clearTimeout(timer); resolve(probe.naturalWidth > 0); };
                probe.onerror = () => { clearTimeout(timer); resolve(false); };
                probe.src = url;
            });
            submit.disabled = false;
            submit.textContent = currentImage ? "Update" : "Insert";
            if (!isImage) {
                error.textContent = "This URL is not an image. Open the image, right-click it and choose \"Copy image address\", then paste that link.";
                error.style.display = "block";
                return;
            }
            const alt = altInput.value.trim();
            const caption = captionInput.value.trim();
            editor.s.restore();

            const image = currentImage || editor.createInside.element("img");
            image.setAttribute("src", url);
            image.setAttribute("alt", alt);
            let figure = currentFigure;
            if (caption) {
                if (!figure) {
                    figure = editor.createInside.element("figure");
                    figure.className = "image";
                    if (currentImage) currentImage.replaceWith(figure);
                    figure.appendChild(image);
                }
                const figcaption = figure.querySelector("figcaption") || figure.appendChild(editor.createInside.element("figcaption"));
                figcaption.textContent = caption;
            } else if (figure) {
                figure.querySelector("figcaption")?.remove();
            }
            if (!currentImage) {
                if (!figure) {
                    figure = editor.createInside.element("figure");
                    figure.className = "image";
                    figure.appendChild(image);
                }
                editor.s.insertNode(figure, true);
            }
            editor.synchronizeValues();
            close();
        });
        setTimeout(() => urlInput.focus(), 0);
        return form;
    },
};

const EMPTY_VALUES = ["<p><br></p>", "<p></p>", "<br>"];

// CKEditor "lark" theme tokens
const CK = {
    border: "hsl(220, 6%, 81%)",
    focus: "hsl(218, 81.8%, 56.9%)",
    hover: "hsl(0, 0%, 94.1%)",
    onBg: "hsl(212, 100%, 97%)",
    text: "hsl(0, 0%, 20%)",
    innerShadow: "2px 2px 3px hsla(0, 0%, 0%, .1) inset",
    font: "Helvetica, Arial, Tahoma, Verdana, Sans-Serif",
};

const CustomEditor = ({
    value = '',
    onChange,
    disabled = false,
    minHeight = 250,
    viewMinHeight = 250,
    config = {},
    sx = {},
    onReady,
    onBlur,
}) => {
    const editorRef = useRef(null);
    const callbacks = useRef({});
    callbacks.current = { onChange, onReady, onBlur };

    // Jodit is re-created whenever the config object changes, so build it once
    const joditConfig = useMemo(() => {
        const settings = { ...DEFAULT_EDITOR_CONFIG, ...config };
        const items = Array.isArray(settings.toolbar) ? settings.toolbar : settings.toolbar?.items || [];
        const buttons = items.map(item => TOOLBAR_MAP[item]).filter(Boolean);
        return {
            readonly: disabled,
            toolbarAdaptive: false,
            toolbarSticky: false, // editor has a fixed height with its own scrollbar, so the toolbar never needs to float over the page
            toolbarButtonSize: "middle",
            buttons, buttonsMD: buttons, buttonsSM: buttons, buttonsXS: buttons,
            statusbar: false,
            hidePoweredByJodit: true,
            showCharsCounter: false,
            showWordsCounter: false,
            showXPathInStatusbar: false,
            askBeforePasteHTML: false,
            askBeforePasteFromWord: false,
            defaultActionOnPaste: "insert_as_html",
            placeholder: settings.placeholder || "",
            minHeight,
            height: "auto",
            allowResizeY: false,
            disablePlugins: "add-new-line,about,ai-assistant,speech-recognize,powered-by-jodit",
            // Images can only be added from a web URL (no upload / base64)
            uploader: { insertImageAsBase64URI: false, showTabInFileSelector: false },
            link: { noFollowCheckbox: false, openInNewTabCheckbox: false },
            controls: {
                imageUrl: imageUrlControl,
                paragraph: {
                    list: Jodit.atom(getHeadingList(settings.heading)),
                    // Show the current block name ("Paragraph", "Heading 1"...) instead of the ¶ icon, like CKEditor
                    update(editor, button) {
                        Jodit.defaultOptions.controls.paragraph.update?.call(this, editor, button);
                        const list = button.control.list || {};
                        button.state.icon.name = "";
                        button.state.text = list[button.state.value] || list.p || "Paragraph";
                        return false;
                    },
                },
                blockquote: {
                    icon: "blockquote",
                    tooltip: "Block quote",
                    exec: editor => editor.execCommand("formatBlock", false, "blockquote"),
                    isActive: editor => Boolean(editor.s.current() && Jodit.modules.Dom.closest(editor.s.current(), "blockquote", editor.editor)),
                },
            },
            ...settings.jodit,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { editorRef.current?.setReadOnly(disabled); }, [disabled]);

    const handleRef = useCallback(editor => {
        editorRef.current = editor;
        if (editor) editor.waitForReady().then(() => callbacks.current.onReady?.(editor));
    }, []);

    const handleChange = useCallback(html => {
        callbacks.current.onChange?.(EMPTY_VALUES.includes(html) ? '' : html, null, editorRef.current);
    }, []);

    const handleBlur = useCallback((html, event) => callbacks.current.onBlur?.(event, editorRef.current), []);

    // Block pasting / dropping image files so images are only added by URL
    const blockFiles = useCallback(event => {
        const data = event.clipboardData || event.dataTransfer;
        if (data?.files?.length) { event.preventDefault(); event.stopPropagation(); }
    }, []);

    const height = disabled ? viewMinHeight : minHeight;

    return (
        <Box onPasteCapture={blockFiles} onDropCapture={blockFiles} sx={{
            "& .jodit-container:not(.jodit_inline)": {
                border: `1px solid ${CK.border}`, borderRadius: "2px", background: "#fff", font: `13px ${CK.font}`,
                "&:focus-within": { borderColor: CK.focus },
                ...(disabled && { borderColor: "action.disabled" }),
            },
            "& .jodit-toolbar__box:not(:empty)": {
                background: "#fff", borderBottom: `1px solid ${CK.border}`, borderRadius: "2px 2px 0 0", overflow: "visible",
            },
            "& .jodit-container:focus-within .jodit-toolbar__box": { borderBottomColor: CK.focus },
            "& .jodit-toolbar-editor-collection_mode_horizontal": { backgroundImage: "none" },
            "& .jodit-toolbar-button__button": { borderRadius: "2px", color: CK.text },
            "& .jodit-toolbar-button:hover:not([disabled]), & .jodit-toolbar-button__button:hover:not([disabled])": { background: CK.hover },
            "& .jodit-toolbar-button_active, & .jodit-toolbar-button__button[aria-pressed=true]": { background: `${CK.onBg} !important`, "& svg": { fill: CK.focus } },
            "& .jodit-toolbar-button_paragraph .jodit-toolbar-button__button": { minWidth: "8.5em", justifyContent: "space-between" },
            "& .jodit-workplace": { height: `${height}px !important`, minHeight: `${height}px !important`, maxHeight: `${height}px !important`, overflowY: "auto !important" },
            "& .jodit-container:focus-within .jodit-workplace": { boxShadow: CK.innerShadow },
            "& .jodit-wysiwyg": {
                minHeight: "100% !important", height: "auto !important", boxSizing: "border-box",
                px: "0.6em !important", py: "1px !important", fontSize: "14px", lineHeight: 1.6, color: CK.text, overflowWrap: "break-word",
                "& p": { my: "1em" },
                // The app forces `* { font-family: Roboto-Regular !important }`, which has no bold weight, so bold text needs the Roboto-Bold face
                "& strong, & b, & th, & h1, & h2, & h3, & h4, & h5, & h6": { fontFamily: '"Roboto-Bold", Helvetica, Arial, sans-serif !important', fontWeight: "normal" },
                "& blockquote": { overflow: "hidden", px: "1.5em", mx: 0, fontStyle: "italic", borderLeft: "solid 5px hsl(0, 0%, 80%)" },
                "& ul, & ol": { pl: "40px" },
                "& img": { maxWidth: "100%", height: "auto" },
                "& figure.image": { display: "block", width: "fit-content", maxWidth: "100%", clear: "both", textAlign: "center", m: "0.9em auto", "& img": { display: "block", mx: "auto", maxWidth: "100%" } },
                "& figure.image > figcaption": { display: "block", wordBreak: "break-word", color: CK.text, background: "hsl(0, 0%, 97%)", p: "0.6em", fontSize: "0.75em", outlineOffset: "-1px" },
                "& table": { borderCollapse: "collapse", borderSpacing: 0, m: "0.9em auto", border: "1px double hsl(0, 0%, 70%)" },
                "& td, & th": { minWidth: "2em", p: "0.4em", border: "1px solid hsl(0, 0%, 75%)", verticalAlign: "top" },
                "& th": { fontWeight: "bold", background: "hsla(0, 0%, 0%, 5%)" },
                "& iframe": { maxWidth: "100%" },
                ...(disabled && {
                    color: "text.disabled",
                    // Links look and behave disabled in view mode (not clickable, same grey as the text)
                    "& a": { color: "text.disabled", pointerEvents: "none", cursor: "default" },
                }),
            },
            "& .jodit-placeholder": { px: "0.6em", color: "hsl(0, 0%, 44%)" },
            "& .jodit-popup__content, & .jodit-ui-list": { font: `13px ${CK.font}` },
            ...sx
        }}>
            <JoditEditor
                ref={handleRef}
                value={value || ''}
                config={joditConfig}
                onChange={handleChange}
                onBlur={handleBlur}
            />
        </Box>
    );
}
export default CustomEditor;
